/**
 * usePoConcentration — fetches the Day 98 (P8.5) calculate_po_concentration RPC
 * for the PO Builder step-4 review (spec § 19 step 4). Reflects the server-side
 * po_staging tray (scoped to auth.uid() + the active distributor, mirroring
 * calculate_po_impact). Refetches when the staging signature (rows / quantities
 * / costs / paths) or the distributor changes.
 *
 * Note: inline allocation edits in POBuilder are local-until-submit (they don't
 * write po_staging.allocations), so by_marketplace reflects the *staged*
 * allocation (add-time), not unsaved edits — a known V1 limitation. Edit-mode
 * concentration (po_line_items, needs a p_po_id variant) is deferred per
 * Checkpoint B; callers pass enabled=false in edit mode.
 */
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Concentration } from '../lib/poConstraints';

/** The full calculate_po_concentration result: the deterministic Concentration
 *  shape (poConstraints) + the session-meta fields. by_sku carries the Day-98.1
 *  raw risk fields (ConcentrationSkuRow), so `concentration.data` is structurally
 *  assignable to evaluateGuardrails / aggregateByRisk with no cast. */
export interface POConcentrationResult extends Concentration {
  distributor_id: string;
  user_id: string | null;
  computed_at: string;
}

export interface UsePoConcentrationResult {
  data: POConcentrationResult | null;
  loading: boolean;
  error: string | null;
}

export function usePoConcentration(
  distributorId: string | null,
  signature: string,
  enabled: boolean
): UsePoConcentrationResult {
  const [data, setData] = useState<POConcentrationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !distributorId || signature === '') {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase
      .rpc('calculate_po_concentration', { p_distributor_id: distributorId })
      .then(({ data: d, error: e }) => {
        if (cancelled) return;
        if (e) {
          console.error('[usePoConcentration] error:', e.message);
          setError('Concentration failed to load. Adjust the tray to retry.');
          setData(null);
        } else {
          setData(d as unknown as POConcentrationResult);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [distributorId, signature, enabled]);

  return { data, loading, error };
}
